































































import { Component, Inject, Mixins, Watch } from "vue-property-decorator";

// Mxins
import Context from "@/mixins/context";
import CountriesStore from "@/mixins/constants/countriesStore";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { ICompany } from "@/models/companies";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { ActionTypes } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      dataScope(state, getters) {
        return getters[`data/${DataModules.CLIENTS_COMPANIES}/clientScope`](
          this.basketData.clientId
        );
      },
      clientCompanies(state, getters) {
        return this.$_.orderBy(
          getters[`data/listArray`]({
            storeModule: DataModules.CLIENTS_COMPANIES,
            scope: this.dataScope
          }),
          ["created_at", "asc"]
        );
      }
    })
  }
})
export default class BasketCompanies extends Mixins(Context, CountriesStore) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Data

  public actionTypes = ActionTypes;
  public clientCompanies!: ICompany[];
  public clientCompany = {} as ICompany;
  public companyId: ICompany["id"] = this.basketCompanyId;
  public dataScope!: string;
  public isLoading = true;

  // Computed

  public get basketData() {
    return this.$basketData();
  }
  public get basketCompanyId() {
    return this.$_.get(this.basketData, "basket.company_id");
  }
  public get basketAddressId() {
    return this.$_.get(this.basketData, "basket.address_id");
  }
  public get company(): ICompany | undefined {
    return this.$_.find(this.clientCompanies, ["id", this.companyId]);
  }

  // Life-cycle methods

  public async created() {
    await this.getClientCompanies();
    if (!this.companyId && this.clientCompanies.length) {
      // Select default company
      this.companyId = this.$_.get(
        // Find by address_id OR default
        this.$_.find(this.clientCompanies, [
          "address_id",
          this.basketAddressId
        ]) || this.$_.find(this.clientCompanies, ["default", true]),
        "id",
        ""
      );
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_COMPANIES,
      scope: this.dataScope,
      vm: this
    });
  }

  // Methods

  public async getClientCompanies() {
    try {
      this.isLoading = true;
      await this.$store.dispatch(`data/${DataModules.CLIENTS_COMPANIES}/list`, {
        id: this.basketData.client?.id || this.basketData.clientId,
        scope: this.dataScope,
        ignoreStored: true,
        vm: this
      });
    } finally {
      this.isLoading = false;
    }
  }

  public openAddCompanyModal({
    company = {} as ICompany,
    actionType = ActionTypes.UPDATE
  }: {
    company: ICompany;
    actionType: ActionTypes;
  }) {
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/client/addEditClientCompanyModal.vue"
          ),
        props: {
          clientId: this.basketData.clientId,
          ...(this.$_.isEmpty(company)
            ? {}
            : {
                data: this.$_.pick(company, [
                  "name",
                  "vat_number",
                  "reg_number",
                  "address_id",
                  "email_id",
                  "phone_id"
                ]),
                actionType,
                companyId:
                  actionType === ActionTypes.UPDATE ? company.id : undefined
              })
        },
        width: 640,
        events: {
          success: async (company: ICompany) => {
            // Reload companies as we need `address` relation
            await this.getClientCompanies();

            if (actionType === ActionTypes.CREATE) {
              // Change ID, triggering updateBasketCompany
              this.companyId = company.id;
            } else if (this.basketCompanyId === company.id) {
              // Reload basket incase VAT amount changed
              await this.$basketMethods.getBasket(true);
            }
          }
        }
      }
    });
  }

  @Watch("companyId")
  public async updateBasketCompany(companyId) {
    try {
      if (this.basketCompanyId !== companyId)
        await this.$basketMethods.updateBasketData({
          company_id: companyId,
          address_id: this.$_.get(this.company, "address_id")
        });
      // Reload basket incase VAT amount changed
      await this.$basketMethods.getBasket(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }
}
